import { classifyPageLayers, normalizeLayerMetadata } from './templateLayers.js'

export const PAGE_WIDTH = 210
export const PAGE_HEIGHT = 297
export const FONTS = ['Times New Roman', 'Arial', 'Calibri', 'Noto Sans']

export const VARIABLES = [
  { key: 'cliente_nome', label: 'Nome do cliente', example: 'Nome do cliente' },
  {
    key: 'cliente_documento',
    label: 'CPF ou CNPJ do cliente',
    example: '000.000.000-00',
  },
  {
    key: 'cliente_endereco',
    label: 'Endereço do cliente',
    example: 'Rua Exemplo, 100',
  },
  {
    key: 'parte_contraria',
    label: 'Parte contrária',
    example: 'Nome da parte contrária',
  },
  {
    key: 'numero_processo',
    label: 'Número do processo',
    example: '0000000-00.2026.8.26.0000',
  },
  { key: 'vara', label: 'Vara ou órgão julgador', example: '1ª Vara Cível' },
  { key: 'comarca', label: 'Comarca', example: 'Comarca de Exemplo' },
  { key: 'tribunal', label: 'Tribunal', example: 'Tribunal de Justiça' },
  {
    key: 'data',
    label: 'Data do documento',
    example: '10 de setembro de 2026',
  },
  {
    key: 'advogado_nome',
    label: 'Nome do advogado',
    example: 'Nome do advogado',
  },
  { key: 'advogado_oab', label: 'OAB do advogado', example: 'OAB/UF 000.000' },
  {
    key: 'escritorio_nome',
    label: 'Nome do escritório',
    example: 'Nome do escritório',
  },
  {
    key: 'escritorio_endereco',
    label: 'Endereço do escritório',
    example: 'Rua Exemplo, 100 · Centro',
  },
  {
    key: 'escritorio_telefone',
    label: 'Telefone do escritório',
    example: '(00) 0000-0000',
  },
  {
    key: 'escritorio_email',
    label: 'E-mail do escritório',
    example: 'contato@exemplo.com.br',
  },
]

const ELEMENT_TYPES = new Set(['text', 'image', 'line'])
const ALIGNMENTS = new Set(['left', 'center', 'right', 'justify'])
const COLOR = /^#[\da-f]{6}$/i
const IMAGE_DATA = /^data:image\/(?:png|jpeg);base64,[a-z\d+/]+={0,2}$/i
let fallbackSequence = 0

function newId() {
  if (globalThis.crypto?.randomUUID) return globalThis.crypto.randomUUID()
  if (globalThis.crypto?.getRandomValues) {
    const bytes = globalThis.crypto.getRandomValues(new Uint8Array(16))
    bytes[6] = (bytes[6] & 15) | 64
    bytes[8] = (bytes[8] & 63) | 128
    const hex = [...bytes]
      .map((value) => value.toString(16).padStart(2, '0'))
      .join('')
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`
  }
  // IDs are document-local identifiers; this branch also supports offline, non-secure previews.
  fallbackSequence += 1
  return `lt-${Date.now().toString(36)}-${fallbackSequence.toString(36)}`
}

function finite(value, fallback) {
  if (value === '' || value === null || value === undefined) return fallback
  const number = Number(value)
  return Number.isFinite(number) ? number : fallback
}

function clamp(value, minimum, maximum) {
  return Math.min(maximum, Math.max(minimum, value))
}

function round(value) {
  return Math.round(value * 100) / 100
}

function validId(value) {
  return typeof value === 'string' && value.trim() && value.length <= 100
    ? value
    : newId()
}

export function isSupportedImageData(value) {
  return typeof value === 'string' && IMAGE_DATA.test(value)
}

export function createElement(type = 'text', overrides = {}) {
  if (!ELEMENT_TYPES.has(type))
    throw new TypeError(`Tipo de elemento não suportado: ${type}`)
  const input = overrides && typeof overrides === 'object' ? overrides : {}
  const defaultWidth = type === 'image' ? 50 : 170
  const defaultHeight = type === 'image' ? 35 : type === 'line' ? 1 : 36
  const width = round(clamp(finite(input.width, defaultWidth), 4, PAGE_WIDTH))
  const height = round(
    clamp(
      finite(input.height, defaultHeight),
      type === 'line' ? 0.25 : 4,
      PAGE_HEIGHT,
    ),
  )

  return {
    id: validId(input.id),
    type,
    ...normalizeLayerMetadata(type, input),
    x: round(clamp(finite(input.x, 20), 0, PAGE_WIDTH - width)),
    y: round(clamp(finite(input.y, 20), 0, PAGE_HEIGHT - height)),
    width,
    height,
    text:
      typeof input.text === 'string'
        ? input.text
        : type === 'text'
          ? 'Digite seu texto aqui.'
          : '',
    imageData: isSupportedImageData(input.imageData) ? input.imageData : '',
    fontFamily: FONTS.includes(input.fontFamily) ? input.fontFamily : FONTS[0],
    fontSize: round(clamp(finite(input.fontSize, 12), 6, 144)),
    bold: input.bold === true,
    italic: input.italic === true,
    underline: input.underline === true,
    color:
      typeof input.color === 'string' && COLOR.test(input.color)
        ? input.color
        : '#17212b',
    alignment: ALIGNMENTS.has(input.alignment) ? input.alignment : 'left',
    lineHeight: round(clamp(finite(input.lineHeight, 1.5), 1, 3)),
    locked: input.locked === true,
  }
}

export function createPage(elements = []) {
  return {
    id: newId(),
    elements: Array.isArray(elements)
      ? classifyPageLayers(
          elements
            .filter((element) => element && ELEMENT_TYPES.has(element.type))
            .map((element) => createElement(element.type, element)),
        )
      : [],
  }
}

export function createDocument(starter = 'blank') {
  const elements = []
  if (starter === 'letterhead' || starter === 'petition') {
    elements.push(
      createElement('text', {
        x: 20,
        y: 15,
        height: 12,
        text: '{{escritorio_nome}}',
        layerRole: 'header',
        fontFamily: 'Arial',
        fontSize: 17,
        bold: true,
        color: '#244b54',
      }),
      createElement('line', { x: 20, y: 30, height: 0.6, color: '#244b54' }),
      createElement('text', {
        x: 20,
        y: 274,
        height: 12,
        text: '{{escritorio_endereco}}\n{{escritorio_telefone}} · {{escritorio_email}}',
        layerRole: 'footer',
        fontFamily: 'Arial',
        fontSize: 8,
        color: '#63717a',
        alignment: 'center',
        lineHeight: 1.3,
      }),
    )
  }
  if (starter === 'petition') {
    elements.push(
      createElement('text', {
        x: 20,
        y: 46,
        height: 26,
        text: 'AO JUÍZO DA {{vara}}\n{{comarca}}',
        layerRole: 'text',
        bold: true,
      }),
      createElement('text', {
        x: 20,
        y: 82,
        height: 14,
        text: 'Processo nº {{numero_processo}}',
      }),
      createElement('text', {
        x: 20,
        y: 106,
        height: 100,
        text: '{{cliente_nome}}\n\nEscreva aqui o conteúdo da peça e adapte-o ao caso concreto.',
        alignment: 'justify',
      }),
      createElement('text', {
        x: 20,
        y: 220,
        height: 37,
        text: '{{comarca}}, {{data}}.\n\n{{advogado_nome}}\n{{advogado_oab}}',
        alignment: 'center',
      }),
    )
  }
  return { schemaVersion: 1, marginMm: 20, pages: [createPage(elements)] }
}

export function normalizeDocument(input) {
  if (!input || typeof input !== 'object') return createDocument()
  const usedIds = new Set()
  const uniqueId = (id) => {
    let next = validId(id)
    while (usedIds.has(next)) next = newId()
    usedIds.add(next)
    return next
  }
  const pages = Array.isArray(input.pages)
    ? input.pages.filter((page) => page && typeof page === 'object')
    : []
  return {
    schemaVersion: 1,
    marginMm: round(clamp(finite(input.marginMm, 20), 0, 50)),
    pages: pages.length
      ? pages.map((page) => ({
          id: uniqueId(page.id),
          elements: classifyPageLayers(
            (Array.isArray(page.elements) ? page.elements : [])
              .filter((element) => element && ELEMENT_TYPES.has(element.type))
              .map((element) =>
                createElement(element.type, {
                  ...element,
                  id: uniqueId(element.id),
                }),
              ),
          ),
        }))
      : [createPage()],
  }
}

export function duplicateElement(element, overrides = {}) {
  return createElement(element.type, {
    ...element,
    x: finite(element.x, 20) + 4,
    y: finite(element.y, 20) + 4,
    ...overrides,
    id: newId(),
  })
}

export function updateElement(document, elementId, patch) {
  if (!document || !Array.isArray(document.pages)) return document
  let changed = false
  const pages = document.pages.map((page) => {
    if (!Array.isArray(page.elements)) return page
    let pageChanged = false
    const elements = page.elements.map((element) => {
      if (element.id !== elementId) return element
      const update = typeof patch === 'function' ? patch(element) : patch
      if (!update || typeof update !== 'object') return element
      const next = createElement(element.type, {
        ...element,
        ...update,
        id: element.id,
      })
      if (Object.keys(next).every((key) => next[key] === element[key]))
        return element
      pageChanged = true
      changed = true
      return next
    })
    return pageChanged ? { ...page, elements } : page
  })
  return changed ? { ...document, pages } : document
}

export function removeElement(document, elementId) {
  if (!document || !Array.isArray(document.pages)) return document
  let changed = false
  const pages = document.pages.map((page) => {
    if (!Array.isArray(page.elements)) return page
    const elements = page.elements.filter((element) => element.id !== elementId)
    if (elements.length === page.elements.length) return page
    changed = true
    return { ...page, elements }
  })
  return changed ? { ...document, pages } : document
}

export function resolveText(text, values = {}) {
  if (typeof text !== 'string') return ''
  return text.replace(/{{\s*([\w.:-]+)\s*}}/g, (token, key) => {
    if (!values || !Object.prototype.hasOwnProperty.call(values, key))
      return token
    const value = values[key]
    if (value === null || value === undefined || value === '') return token
    return typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
      ? String(value)
      : token
  })
}
